import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, Trash2, LogOut, Inbox as InboxIcon, CheckCircle2, Check, X, Eye, EyeOff, AlertCircle, CalendarDays, Images } from "lucide-react";
import { Logo } from "./Logo";
import InboxCalendar from "./InboxCalendar";
import GalleryPanel from "./GalleryPanel";
import ReviewsPanel from "./ReviewsPanel";
import {
  checkOwner,
  signInWithGoogle,
  signInWithPassword,
  forgotPassword,
  formatApiErrorDetail,
  getSavedPin,
  savePin,
  clearPin,
} from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  accepted: "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30",
  denied: "bg-red-500/15 text-red-300 border border-red-400/30",
  cancelled: "bg-slate-400/15 text-slate-300 border border-slate-400/30",
  pending: "bg-amber-400/15 text-amber-300 border border-amber-400/30",
};

const STATUS_LABELS = { accepted: "Accepted", denied: "Denied", cancelled: "Cancelled", pending: "Pending" };

const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function Inbox() {
  const [authed, setAuthed] = useState(false);
  const [authMethod, setAuthMethod] = useState(null);
  const [checking, setChecking] = useState(true);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [denyingId, setDenyingId] = useState(null);
  const [denyReason, setDenyReason] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [authErr, setAuthErr] = useState("");
  const [authMode, setAuthMode] = useState("pin");
  const [forgotSent, setForgotSent] = useState(false);
  const [actionErr, setActionErr] = useState("");

  const fetchBookings = useCallback(async (method, p) => {
    setBusy(true);
    try {
      const headers = method === "pin" ? { "x-schedule-pin": p } : {};
      const { data } = await axios.get(`${API}/contact`, { headers });
      setBookings(data);
      setLoaded(true);
      setActionErr("");
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        setAuthMethod(null);
        clearPin();
      }
      setLoaded(true);
    }
    setBusy(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await checkOwner();
      if (cancelled) return;
      if (user) {
        setAuthMethod("google");
        setAuthed(true);
        fetchBookings("google");
      } else {
        // "Keep me signed in" — a remembered PIN re-unlocks without asking.
        const saved = getSavedPin();
        if (saved) {
          try {
            await axios.get(`${API}/schedule/pin-check`, { headers: { "x-schedule-pin": saved } });
            if (cancelled) return;
            setPin(saved);
            setAuthMethod("pin");
            setAuthed(true);
            fetchBookings("pin", saved);
          } catch {
            if (!cancelled) clearPin();
          }
        }
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchBookings]);

  const unlock = async (e) => {
    e.preventDefault();
    const entered = pin.replace(/\s/g, "").trim();
    if (!entered) {
      setPinError(true);
      return;
    }
    setBusy(true);
    try {
      await axios.get(`${API}/schedule/pin-check`, { headers: { "x-schedule-pin": entered } });
      savePin(entered);
      setPin(entered);
      setAuthMethod("pin");
      setAuthed(true);
      setPinError(false);
      fetchBookings("pin", entered);
    } catch {
      setPinError(true);
    }
    setBusy(false);
  };

  const signInEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    setAuthErr("");
    try {
      await signInWithPassword(email, pw);
      const user = await checkOwner();
      if (user) {
        setAuthMethod("password");
        setAuthed(true);
        fetchBookings("password");
      } else {
        setAuthErr("This account doesn't have owner access.");
      }
    } catch (err) {
      setAuthErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setAuthErr("");
    try {
      await forgotPassword(email);
      setForgotSent(true);
    } catch (err) {
      setAuthErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const switchMode = (mode) => {
    setAuthMode(mode);
    setAuthErr("");
    setPinError(false);
    setForgotSent(false);
  };

  const remove = async (id) => {
    if (busy) return;
    setBusy(true);
    setActionErr("");
    try {
      const headers = authMethod === "pin" ? { "x-schedule-pin": getSavedPin() } : {};
      await axios.delete(`${API}/contact/${id}`, { headers });
      setBookings((b) => b.filter((x) => x.id !== id));
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        setAuthMethod(null);
        clearPin();
      } else {
        setActionErr("That didn't go through — check your connection and try again.");
      }
    }
    setBusy(false);
  };

  const act = async (id, action) => {
    if (busy) return;
    setBusy(true);
    setActionErr("");
    try {
      const headers = authMethod === "pin" ? { "x-schedule-pin": getSavedPin() } : {};
      const body = action === "deny" ? { reason: denyReason.trim() } : {};
      await axios.post(`${API}/contact/${id}/${action}`, body, { headers });
      await fetchBookings(authMethod, getSavedPin());
      setDenyingId(null);
      setDenyReason("");
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        setAuthMethod(null);
        clearPin();
      } else {
        setActionErr("That didn't go through — the booking wasn't changed. Try again.");
      }
    }
    setBusy(false);
  };

  const sendBalance = async (id) => {
    if (busy) return;
    setBusy(true);
    setActionErr("");
    try {
      const headers = authMethod === "pin" ? { "x-schedule-pin": getSavedPin() } : {};
      await axios.post(`${API}/contact/${id}/send-balance`, {}, { headers });
      await fetchBookings(authMethod, getSavedPin());
    } catch (err) {
      if (err.response?.status === 401) {
        setAuthed(false);
        setAuthMethod(null);
        clearPin();
      } else {
        setActionErr("That didn't go through — check your connection and try again.");
      }
    }
    setBusy(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    clearPin();
    setAuthed(false);
    setAuthMethod(null);
    setPin("");
    setPw("");
    setLoaded(false);
    setActionErr("");
  };

  const fmt = (iso) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink font-body text-white" data-testid="inbox-page">
        <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
          <Lock className="h-5 w-5 animate-pulse text-accentlight" /> Checking session…
        </p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6 py-12 font-body text-white" data-testid="inbox-page">
        <div className="w-full max-w-sm rounded-2xl border border-white/5 bg-surface p-8 text-center">
          <span className="text-accentlight">
            <Logo className="mx-auto h-12 w-12" />
          </span>
          <h1 className="mt-5 font-display text-3xl tracking-wider">WELCOME BACK</h1>
          <p className="mt-2 text-sm text-white/50">Sign in to accept bookings and manage your schedule.</p>

          <button
            type="button"
            onClick={() => signInWithGoogle("/inbox")}
            data-testid="inbox-google-button"
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-white py-3.5 text-sm font-bold text-[#1f1f1f] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_10px_30px_-10px_rgba(255,255,255,0.4)]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.6z" />
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.8-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5H1.4v3C3.3 21.3 7.3 24 12 24z" />
              <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4v-3H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.8-3z" />
              <path fill="#EA4335" d="M12 4.8c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 5.8 2.1 4.6 5H1.4v3H8z" />
            </svg>
            Continue with Google
          </button>
          <p className="mt-2 text-[11px] text-white/35">Use your gamelensphotos@gmail.com Google account.</p>

          <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/30">
            <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
          </div>

          {authMode === "pin" && (
            <form onSubmit={unlock} data-testid="inbox-pin-form">
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter your PIN"
                required
                data-testid="inbox-pin-input"
                className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-center text-lg tracking-[0.5em] text-white placeholder:text-base placeholder:tracking-normal placeholder:text-white/30 outline-none focus:border-accentlight"
              />
              {pinError && (
                <p className="mt-3 text-xs text-red-300" data-testid="inbox-pin-error">
                  That PIN didn't work — try again.
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="inbox-pin-submit-button"
                className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
              >
                Unlock inbox
              </button>
            </form>
          )}

          {authMode === "password" && (
            <form onSubmit={signInEmail} data-testid="inbox-email-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                required
                data-testid="inbox-email-input"
                className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
              />
              <div className="relative mt-3">
                <input
                  type={showPw ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  data-testid="inbox-password-input"
                  className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 pr-11 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="inbox-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition-colors hover:text-white"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {authErr && (
                <p className="mt-3 text-xs text-red-300" data-testid="inbox-email-error">
                  {authErr}
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="inbox-email-signin-button"
                className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => switchMode("forgot")}
                data-testid="inbox-forgot-link"
                className="mt-3 text-xs text-white/40 transition-colors hover:text-accentlight"
              >
                Forgot password?
              </button>
            </form>
          )}

          {authMode === "forgot" && (
            <form onSubmit={submitForgot} data-testid="inbox-forgot-form">
              {forgotSent ? (
                <p
                  className="mt-1 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-200"
                  data-testid="inbox-forgot-sent"
                >
                  Reset link sent — check your email. It expires in 1 hour.
                </p>
              ) : (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    autoComplete="email"
                    required
                    data-testid="inbox-forgot-email-input"
                    className="w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
                  />
                  {authErr && (
                    <p className="mt-3 text-xs text-red-300" data-testid="inbox-forgot-error">
                      {authErr}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="inbox-forgot-submit-button"
                    className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
                  >
                    Email me a reset link
                  </button>
                </>
              )}
            </form>
          )}

          {authMode !== "forgot" && (
            <button
              type="button"
              onClick={() => switchMode(authMode === "pin" ? "password" : "pin")}
              data-testid="inbox-pin-toggle-link"
              className="mt-4 text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
            >
              {authMode === "pin" ? "Sign in with email & password instead" : "Use PIN instead"}
            </button>
          )}

          {authMode === "forgot" && (
            <button
              type="button"
              onClick={() => switchMode("pin")}
              data-testid="inbox-back-to-sign-in"
              className="mt-4 text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
            >
              Back to sign in
            </button>
          )}

          <a
            href="/"
            className="mt-4 block text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
          >
            Back to site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink font-body text-white" data-testid="inbox-page">
      <header className="border-b border-white/5 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="text-accentlight">
              <Logo className="h-8 w-8" />
            </span>
            <div className="leading-none">
              <p className="font-display text-xl tracking-wider">
                BOOKING <span className="text-accentlight">INBOX</span>
              </p>
              <p className="text-[9px] font-semibold uppercase tracking-[0.4em] text-white/40">
                GameLens Photography
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="/gallery-hub"
              data-testid="inbox-gallery-link"
              aria-label="The Gallery"
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-accentlight hover:text-accentlight sm:px-4"
            >
              <Images className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Gallery</span>
            </a>
            <a
              href="/?manage"
              data-testid="inbox-schedule-link"
              aria-label="Schedule"
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-accentlight hover:text-accentlight sm:px-4"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Schedule</span>
            </a>
            <button
              onClick={() => fetchBookings(authMethod, getSavedPin())}
              data-testid="inbox-refresh-button"
              aria-label="Refresh"
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-accentlight hover:text-accentlight sm:px-4"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={logout}
              data-testid="inbox-logout-button"
              aria-label="Lock inbox"
              className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-accentlight hover:text-accentlight sm:px-4"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Lock</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        <InboxCalendar bookings={bookings} />

        <ReviewsPanel authMethod={authMethod} />

        {actionErr && (
          <div
            className="mb-6 flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
            data-testid="inbox-action-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" /> {actionErr}
          </div>
        )}

        <p className="mb-6 text-sm text-white/50" data-testid="inbox-count">
          {bookings.length} booking request{bookings.length === 1 ? "" : "s"} received
        </p>

        {loaded && bookings.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-white/10 py-20 text-center" data-testid="inbox-empty-message">
            <InboxIcon className="h-10 w-10 text-white/20" strokeWidth={1.5} />
            <p className="text-white/50">No booking requests yet.</p>
            <p className="text-xs text-white/30">New requests from your booking form land here — and in your email.</p>
          </div>
        )}

        <div className="space-y-4">
          {bookings.map((b) => (
            <article
              key={b.id}
              data-testid={`inbox-row-${b.id}`}
              className={`rounded-2xl border border-white/5 bg-surface p-6 transition-colors duration-300 hover:border-accent/30 md:p-8 ${b.status === "denied" || b.status === "cancelled" ? "opacity-55" : ""}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl tracking-wider" data-testid={`inbox-name-${b.id}`}>
                    {b.name}
                  </h2>
                  <p className="mt-1 text-xs text-white/40">Received {fmt(b.created_at)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={`rounded-full px-4 py-1.5 text-xs font-bold ${STATUS_STYLES[b.status] || STATUS_STYLES.pending}`}
                    data-testid={`inbox-status-${b.id}`}
                  >
                    {STATUS_LABELS[b.status] || "Pending"}
                  </span>
                  {b.package && (
                    <span className="rounded-full bg-gradient-to-r from-accent/20 to-accentlight/20 px-4 py-1.5 text-xs font-bold text-accentlight" data-testid={`inbox-package-${b.id}`}>
                      {b.package}
                    </span>
                  )}
                  <button
                    onClick={() => remove(b.id)}
                    data-testid={`inbox-delete-${b.id}`}
                    aria-label={`Delete request from ${b.name}`}
                    className="rounded-lg border border-white/10 p-2.5 text-white/40 transition-colors hover:border-red-400/50 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                <a href={`mailto:${b.email}`} data-testid={`inbox-email-${b.id}`} className="text-accentlight hover:underline">
                  {b.email}
                </a>
                {b.phone && <p className="text-white/70">{b.phone}</p>}
                {b.game_date && (
                  <p className="text-white/70">
                    Game date: <span className="font-semibold text-white">{b.game_date}</span>
                    {b.game_time ? ` · ${b.game_time}` : ""}
                  </p>
                )}
                {b.team && (
                  <p className="text-white/70">
                    Team: <span className="font-semibold text-white">{b.team}</span>
                  </p>
                )}
                {b.league && (
                  <p className="text-white/70">
                    League: <span className="font-semibold text-white">{b.league}</span>
                  </p>
                )}
                {b.field_location && (
                  <p className="text-white/70">
                    Field: <span className="font-semibold text-white">{b.field_location}</span>
                  </p>
                )}
              </div>

              {b.message && (
                <p className="mt-4 rounded-xl bg-ink p-4 text-sm leading-relaxed text-white/70" data-testid={`inbox-message-${b.id}`}>
                  <span className="mr-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">Notes</span>
                  {b.message}
                </p>
              )}

              {(!b.status || b.status === "pending") && (
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    onClick={() => act(b.id, "accept")}
                    disabled={busy}
                    data-testid={`inbox-accept-${b.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:brightness-110 disabled:opacity-60"
                  >
                    <Check className="h-4 w-4" />
                    {!b.game_date
                      ? "Accept"
                      : b.game_date.includes(",")
                      ? `Accept & block ${b.game_date.split(",").length} dates`
                      : `Accept & block ${b.game_date}`}
                  </button>
                  <button
                    onClick={() => {
                      setDenyingId(b.id);
                      setDenyReason("");
                    }}
                    disabled={busy}
                    data-testid={`inbox-deny-${b.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-red-400/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-red-300 transition-all duration-300 hover:bg-red-500/10 disabled:opacity-60"
                  >
                    <X className="h-4 w-4" /> Deny
                  </button>
                </div>
              )}

              {denyingId === b.id && (
                <div
                  className="mt-3 rounded-xl border border-red-400/20 bg-ink p-4"
                  data-testid={`inbox-deny-panel-${b.id}`}
                >
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                    Reason (sent to the customer)
                  </label>
                  <textarea
                    rows={2}
                    value={denyReason}
                    onChange={(e) => setDenyReason(e.target.value)}
                    placeholder="e.g. Already covering a tournament that weekend"
                    data-testid={`inbox-deny-reason-${b.id}`}
                    className="w-full resize-none rounded-lg border border-white/10 bg-surface px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
                  />
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button
                      onClick={() => act(b.id, "deny")}
                      disabled={busy}
                      data-testid={`inbox-deny-confirm-${b.id}`}
                      className="rounded-full bg-red-500/90 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-red-500 disabled:opacity-60"
                    >
                      Send denial
                    </button>
                    <button
                      onClick={() => {
                        setDenyingId(null);
                        setDenyReason("");
                      }}
                      data-testid={`inbox-deny-cancel-${b.id}`}
                      className="rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white/60 transition-colors hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {b.status === "accepted" && (
                <p className="mt-4 flex items-start gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300" data-testid={`inbox-accepted-note-${b.id}`}>
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {b.game_date ? `${b.game_date} is blocked on your calendar` : "Marked as booked"} — confirmation emailed to <span className="break-all">{b.email}</span>
                  </span>
                </p>
              )}

              {b.status === "denied" && (
                <p className="mt-4 text-xs text-white/45" data-testid={`inbox-denied-note-${b.id}`}>
                  {b.denial_reason ? <>Reason sent to <span className="break-all">{b.email}</span>: {b.denial_reason}</> : <>Denial emailed to <span className="break-all">{b.email}</span>.</>}
                </p>
              )}

              {b.status === "accepted" && (
                <div className="mt-5 flex flex-wrap items-center gap-3" data-testid={`inbox-payments-${b.id}`}>
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold ${
                      b.deposit_paid
                        ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                        : "bg-amber-400/15 text-amber-300 border border-amber-400/30"
                    }`}
                    data-testid={`inbox-deposit-status-${b.id}`}
                  >
                    {b.deposit_paid ? "Deposit received" : `Deposit due ${fmtMoney(b.deposit_due)}`}
                  </span>
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold ${
                      b.balance_paid
                        ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                        : "border border-white/10 bg-surface-hover text-white/60"
                    }`}
                    data-testid={`inbox-balance-status-${b.id}`}
                  >
                    {b.balance_paid ? "Balance received" : `Balance due ${fmtMoney(b.balance_due)}`}
                  </span>
                  {b.promo_permission && (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${
                        b.promo_permission === "granted"
                          ? "bg-accentlight/15 text-accentlight border border-accentlight/40"
                          : "bg-surface-hover text-white/50 border border-white/10"
                      }`}
                      data-testid={`inbox-promo-${b.id}`}
                    >
                      {b.promo_permission === "granted" ? "Photos OK to share" : "Photos: keep private"}
                    </span>
                  )}
                  {!b.balance_paid && (
                    <button
                      onClick={() => sendBalance(b.id)}
                      disabled={busy}
                      data-testid={`inbox-send-balance-${b.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-accentlight/40 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-accentlight transition-all hover:bg-accentlight/10 disabled:opacity-60"
                    >
                      {b.balance_invoice_sent ? "Resend photo delivery email" : "Photos delivered — send balance invoice"}
                    </button>
                  )}
                </div>
              )}

              {b.status === "accepted" && <GalleryPanel booking={b} authMethod={authMethod} />}

              {b.emails && Object.keys(b.emails).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid={`inbox-email-trail-${b.id}`}>
                  {[
                    ["alert", "Booking alert sent"],
                    ["confirmation", "Confirmation emailed"],
                    ["denial", "Denial emailed"],
                    ["balance_invoice", "Balance invoice emailed"],
                  ].map(([key, label]) =>
                    b.emails?.[key] ? (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-surface-hover px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-white/50"
                        data-testid={`inbox-trail-${key}-${b.id}`}
                      >
                        <CheckCircle2 className="h-3 w-3 text-accentlight" />
                        {label} · {fmt(b.emails[key])}
                      </span>
                    ) : null
                  )}
                </div>
              )}
            </article>
          ))}
        </div>

        <p className="mt-10 flex items-center justify-center gap-2 text-xs text-white/30">
          <CheckCircle2 className="h-3.5 w-3.5" /> New requests also arrive as emails at GameLensPhotos@gmail.com
        </p>
        <p className="mt-3 text-center text-[11px] text-white/25" data-testid="inbox-homescreen-tip">
          One-tap game-day check-ins: add this page to your home screen (Share → Add to Home Screen).
        </p>
      </main>
    </div>
  );
}